import { useState, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Logo, Button } from '@resolution-processing/balance';
import { CommentCard } from '@resolution-processing/comment-card';
import { useAuth } from './auth/AuthContext.tsx';
import { LoginScreen } from './components/LoginScreen.tsx';
import { SuiteSwitcher } from './components/SuiteSwitcher.tsx';
import { FileInput } from './components/FileInput.tsx';
import { ResultsList, type NotePostedMeta } from './components/ResultsList.tsx';
import { Alert, type AlertSeverity } from './components/Alert.tsx';
import { runQcCheck, getRun, getHistory, getSuites, detectSuite, type QcRunResponse, type SuiteSummary } from './api.ts';
import styles from './App.module.css';

/** A run/load failure surfaced in the notices region (not a raw API string). */
type RunError = { kind: 'contactUnavailable'; contactId: string } | { kind: 'generic' };

// The URL path segments are positional: /{suiteId}/{contactId}/{runId}.
// A runId (third segment) deep-links to a specific stored run and is VIEWED
// (fetched + displayed) rather than re-run.
const DEFAULT_SUITE_ID = 'advocacy24';

// Persist the analyst's last-selected program so a fresh visit (no suite in the
// URL) reopens on it instead of the hardcoded default. Affiliates who swap
// between programs land back where they left off.
const SUITE_PREF_KEY = 'qc:preferredSuite';

function getStoredSuiteId(): string | null {
  try {
    return window.localStorage.getItem(SUITE_PREF_KEY);
  } catch {
    return null; // localStorage unavailable (private mode / disabled)
  }
}

function storeSuiteId(suiteId: string): void {
  try {
    window.localStorage.setItem(SUITE_PREF_KEY, suiteId);
  } catch {
    /* best-effort — sticky selection is a nicety, not required */
  }
}

function parsePathname(pathname: string): { suiteId: string; contactId: string | null; runId: string | null } {
  const [, first, second, third] = pathname.split('/');
  // An explicit suite in the URL always wins; otherwise use the last-selected
  // program, then fall back to the default.
  const suiteId = first || getStoredSuiteId() || DEFAULT_SUITE_ID;
  return { suiteId, contactId: second || null, runId: third || null };
}

function getSuiteTitleFromPath(pathname: string): string {
  const { suiteId } = parsePathname(pathname);
  const suites = ((window as unknown as Record<string, unknown>).__QC_SUITES__ ?? []) as Array<{ suiteId: string; displayName: string }>;
  return suites.find(s => s.suiteId === suiteId)?.displayName ?? 'QC Engine';
}

export function App() {
  const { t } = useTranslation('ui');
  const { user, loading: authLoading, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  // 'run' = executing a fresh QC run (show "Running checks…"); 'view' = loading a
  // stored run to view (show a generic spinner only — no misleading run text).
  const [loadingMode, setLoadingMode] = useState<'run' | 'view'>('run');
  const [runError, setRunError] = useState<RunError | null>(null);
  // Bumped to pull focus back to the contact input on Start Over (closes the run→submit→
  // start-over→run muscle-memory loop).
  const [focusSignal, setFocusSignal] = useState(0);
  const [currentRun, setCurrentRun] = useState<QcRunResponse | null>(null);
  const [history, setHistory] = useState<QcRunResponse[]>([]);
  const [lastContactId, setLastContactId] = useState<string | null>(null);
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [suites, setSuites] = useState<SuiteSummary[]>([]);
  const [suite, setSuite] = useState<SuiteSummary | null>(null);
  // Pre-flight suite detection banner: 'switch' when the contact's program differs
  // from the selected suite, 'unknown' when the program can't be determined.
  const [detection, setDetection] = useState<
    | { mode: 'switch'; suiteId: string; displayName: string }
    | { mode: 'unknown' }
    | null
  >(null);
  // The resolved contact id + whether it was normalized from a pasted URL/prefix,
  // for the "Detected Contact ID" hint shown alongside detection.
  const [contactInfo, setContactInfo] = useState<{ contactId: string; normalized: boolean } | null>(null);
  const detectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detectAbort = useRef<AbortController | null>(null);
  // Capture URL values once on mount — re-deriving on every render would feed
  // pushState mutations back into the auto-run effect, causing a double run.
  const [{ suiteId: urlSuiteId, contactId: urlContactId, runId: urlRunId }] = useState(
    () => parsePathname(window.location.pathname)
  );
  const hasAutoRun = useRef(false);

  useEffect(() => {
    if (!user) return;
    // Preserve the contact id when redirecting an unknown/legacy suite slug
    // (e.g. an old /enrollment/123 link) to the default suite.
    const fallback = `/${DEFAULT_SUITE_ID}${urlContactId ? `/${urlContactId}` : ''}`;
    getSuites().then((list) => {
      setSuites(list);
      const match = list.find((s) => s.suiteId === urlSuiteId);
      if (match) {
        setSuite(match);
      } else {
        window.location.replace(fallback);
      }
    }).catch(() => window.location.replace(fallback));
  }, [user]);

  // Remember the active program so the next fresh visit reopens on it. Covers the
  // switcher, detection-driven switches, and deep links alike. See parsePathname.
  useEffect(() => {
    if (suite) storeSuiteId(suite.suiteId);
  }, [suite]);

  const handleClear = useCallback(() => {
    setCurrentRun(null);
    setHistory([]);
    setLastContactId(null);
    setLastRunId(null);
    setRunError(null);
    setDetection(null);
    setContactInfo(null);
    // Return focus to the input so the next run starts without hunting for the box.
    setFocusSignal((n) => n + 1);
    window.history.replaceState({}, '', `/${suite?.suiteId ?? DEFAULT_SUITE_ID}`);
  }, [suite]);

  // Debounced pre-flight: resolve the contact's program and surface a banner if it
  // doesn't match the selected suite (or can't be determined).
  const handleContactIdChange = useCallback((info: { contactId: string; normalized: boolean }) => {
    setContactInfo(info.contactId.trim() ? info : null);
    if (detectTimer.current) clearTimeout(detectTimer.current);
    detectAbort.current?.abort();
    if (!info.contactId.trim() || !suite) {
      setDetection(null);
      return;
    }
    detectTimer.current = setTimeout(() => {
      const controller = new AbortController();
      detectAbort.current = controller;
      detectSuite(info.contactId, controller.signal)
        .then((d) => {
          if (d.suiteId && d.displayName && d.suiteId !== suite.suiteId) {
            setDetection({ mode: 'switch', suiteId: d.suiteId, displayName: d.displayName });
          } else if (!d.suiteId) {
            setDetection({ mode: 'unknown' });
          } else {
            setDetection(null);
          }
        })
        .catch((err) => {
          if (err?.name !== 'AbortError') setDetection(null);
        });
    }, 450);
  }, [suite]);

  const handleSuiteChange = useCallback((newSuiteId: string) => {
    const next = suites.find((s) => s.suiteId === newSuiteId);
    if (!next || next.suiteId === suite?.suiteId) return;
    // Switching suites invalidates the current results. Clear them (the contact ID
    // stays in the input) so the analyst can re-run under the newly selected suite.
    hasAutoRun.current = true;
    setSuite(next);
    setCurrentRun(null);
    setHistory([]);
    setLastContactId(null);
    setLastRunId(null);
    setRunError(null);
    setDetection(null);
    window.history.replaceState({}, '', `/${next.suiteId}`);
  }, [suites, suite]);

  const handleRun = useCallback(async (contactId: string) => {
    hasAutoRun.current = true;
    if (!suite) return;
    setLoadingMode('run');
    setLoading(true);
    setRunError(null);
    setDetection(null);
    setCurrentRun(null);

    try {
      const result = await runQcCheck(contactId, suite.suiteId);
      setCurrentRun(result);
      setLastContactId(contactId);
      setLastRunId(result.runId);
      const targetPath = `/${suite.suiteId}/${contactId}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState({}, '', targetPath);
      }

      // Fetch history in background, scoped to the active suite so runs from a
      // different suite don't intermingle for the same contact. The current run
      // is kept in the list and highlighted rather than filtered out.
      getHistory(contactId, suite.suiteId)
        .then((h) => setHistory(h.runs))
        .catch(() => {});
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      // The run endpoint returns the 'contact_unavailable' code when Forth can't
      // resolve the contact; everything else is a generic run failure.
      setRunError(message === 'contact_unavailable'
        ? { kind: 'contactUnavailable', contactId }
        : { kind: 'generic' });
    } finally {
      setLoading(false);
    }
  }, [suite]);

  // View a specific stored run (deep link or history selection) WITHOUT starting a
  // new run. The contact card is refetched live server-side. lastRunId is left
  // untouched so the "Post Results to Forth" action stays hidden for historical runs.
  const loadRun = useCallback(async (contactId: string, runId: string) => {
    hasAutoRun.current = true;
    if (!suite) return;
    setLoadingMode('view');
    setLoading(true);
    setRunError(null);
    setCurrentRun(null);

    try {
      const result = await getRun(contactId, runId);
      setCurrentRun(result);
      setLastContactId(contactId);
      const targetPath = `/${suite.suiteId}/${contactId}/${runId}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState({}, '', targetPath);
      }
      getHistory(contactId, suite.suiteId)
        .then((h) => setHistory(h.runs))
        .catch(() => {});
    } catch {
      setRunError({ kind: 'generic' });
    } finally {
      setLoading(false);
    }
  }, [suite]);

  useEffect(() => {
    if (!suite || hasAutoRun.current) return;
    // A runId in the URL deep-links to a stored run: view it, don't re-run.
    if (urlContactId && urlRunId) {
      hasAutoRun.current = true;
      loadRun(urlContactId, urlRunId);
    } else if (urlContactId) {
      hasAutoRun.current = true;
      handleRun(urlContactId);
    }
  }, [suite, urlContactId, urlRunId, handleRun, loadRun]);

  useEffect(() => {
    const onPopState = () => {
      const { contactId: newId, runId: newRunId } = parsePathname(window.location.pathname);
      if (newId && newRunId) {
        loadRun(newId, newRunId);
      } else if (newId) {
        handleRun(newId);
      } else {
        setCurrentRun(null);
        setHistory([]);
        setLastContactId(null);
        setLastRunId(null);
        setRunError(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [handleRun, loadRun]);

  if (authLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (!user) {
    return <LoginScreen title={getSuiteTitleFromPath(window.location.pathname)} />;
  }

  if (!suite) {
    return <div className={styles.loading}>Loading...</div>;
  }

  // One notice at a time for the shared region, by severity precedence: error > warning > info.
  // (Sources are largely mutually exclusive. Submit errors are NOT here — they render inline in
  // ResultActions, next to the Retry button, rather than lifting up to this region.)
  const activeNotice: { severity: AlertSeverity; text: string; action?: { label: string; onClick: () => void } } | null =
    runError
      ? {
          severity: 'error',
          text: runError.kind === 'contactUnavailable'
            ? t('alert.contactUnavailable', { contactId: runError.contactId })
            : t('alert.runFailed'),
        }
      : detection?.mode === 'unknown'
      ? { severity: 'warning', text: t('detect.unknown') }
      : detection?.mode === 'switch'
      ? {
          severity: 'info',
          text: t('detect.switchPrompt', { suite: detection.displayName }),
          action: { label: t('detect.switchAction'), onClick: () => handleSuiteChange(detection.suiteId) },
        }
      : null;

  return (
    <Sentry.ErrorBoundary fallback={<div className={styles.error}>Something went wrong. Please refresh and try again.</div>}>
      {/* In-app feedback/bug widget for all authenticated users. Renders nothing itself —
          mounts a floating button in its own Shadow DOM and files reports as GitHub issues
          via the relay. Private repo + internal tool, so default log capture is acceptable. */}
      <CommentCard
        repo="resolution-processing/qc-engine"
        titlePrefix="[QC]"
        labels={['feedback', 'qc-engine']}
      />
      <div className={styles.app}>
        <header className={styles.header}>
          <a href="/" className={styles.logoLink}><Logo color="white" height={32} /></a>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{user.name}</span>
            <Button variant="invisible" className={styles.logoutButton} onClick={logout}>Logout</Button>
          </div>
        </header>
        <main className={styles.main}>
          <SuiteSwitcher
            suites={suites}
            current={suite}
            onSelect={handleSuiteChange}
            disabled={loading}
          />
          <FileInput
            onSubmit={handleRun}
            onClear={handleClear}
            loading={loading}
            clearable={!!(currentRun || runError)}
            initialValue={urlContactId ?? undefined}
            onContactIdChange={handleContactIdChange}
            focusSignal={focusSignal}
          />

          {/* Input-adjacent confirmation of a normalized contact id — a hint, not a notice. */}
          {contactInfo?.normalized && (
            <div className={styles.infoRow}>
              <span className={styles.infoItem}>
                ℹ️ {t('input.detectedContactId')}: <strong>{contactInfo.contactId}</strong>
              </span>
            </div>
          )}

          {/* Shared notices region: exactly one alert, chosen by severity precedence. */}
          {activeNotice && (
            <Alert severity={activeNotice.severity} action={activeNotice.action}>
              {activeNotice.text}
            </Alert>
          )}

          {loading && (
            <div className={styles.loading}>
              <span className={styles.spinner} aria-hidden="true" />
              {loadingMode === 'run' && <span>{t('loading.checks')}</span>}
            </div>
          )}

          {currentRun && (
            <ResultsList
              run={currentRun}
              allowPostNote={currentRun.runId === lastRunId}
              history={history}
              onSelectRun={(run) => loadRun(run.contactId, run.runId)}
              onRerun={() => handleRun(currentRun.contactId)}
              onStartOver={handleClear}
              onPosted={(meta: NotePostedMeta) => {
                const posted = { notePosted: true as const, ...meta };
                setCurrentRun((prev) =>
                  prev && prev.runId === currentRun.runId ? { ...prev, ...posted } : prev,
                );
                setHistory((prev) =>
                  prev.map((r) => (r.runId === currentRun.runId ? { ...r, ...posted } : r)),
                );
              }}
            />
          )}
        </main>
      </div>
    </Sentry.ErrorBoundary>
  );
}
